import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  Stack,
  Modal,
} from '@mui/material';

import {
  deleteUser,
  fetchUsers,
  markMutationAsIdle,
  setPage,
  setPageSize,
} from '../../actions/UserActions';
import PageHeader from '../../components/PageHeader';
import { hasPermission } from '../../utils/common';
import {
  permissionsList,
  permissionsWithAssignedIds,
} from '../../api/constant';
import { toast } from 'sonner';
import { MutationStatuses } from '../../reducers/constants';
import UIModal from '../../components/UIElements/UIModal';
import { useNavigate } from 'react-router-dom';
import ProtectedComponent from '../../common/ProtectedComponent';

export default function ViewAllUsers() {
  const [userToBeDeleted, setUserToBeDeleted] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { users, totalUsers, page, pageSize, loading, error, mutationStatus } =
    useSelector((state) => state.userReducer);
  const permissionMap = useSelector(
    (state) => state.loginReducer?.permissionsMap,
  );

  useEffect(() => {
    toast.promise(dispatch(fetchUsers(page, pageSize)), {
      loading: 'Loading users...',
      success: 'Users Loaded',
      error: 'Failed to load users',
    });
  }, [dispatch, page, pageSize]);

  useEffect(() => {
    if (mutationStatus === MutationStatuses.SUCCESS) {
      toast.promise(dispatch(fetchUsers(page, pageSize)), {
        loading: 'Loading users...',
        success: 'Users Loaded',
        error: 'Failed to load users',
      });
      setUserToBeDeleted(null);
      dispatch(markMutationAsIdle());
    }
  }, [mutationStatus]);

  const handlePageChange = (event, value) => {
    dispatch(setPage(value));
  };

  const handlePageSizeChange = (event) => {
    dispatch(setPageSize(event.target.value));
  };

  const handleDeleteUser = (userId) => {
    toast.promise(dispatch(deleteUser(userId)), {
      loading: 'Deleting user ...',
      success: 'User Deleted Successfully',
      error: 'Failed to delete user',
      duration: 100,
    });
    setUserToBeDeleted(null);
  };

  return (<ProtectedComponent
      requiredPermission={
        permissionsWithAssignedIds[permissionsList.CreateUser]
      }
    >
          <UIModal
            title="Please confirm ?"
            body="Once deleted, this cannot be undone"
            proceedText="Confirm"
            cancelText="Cancel"
            show={userToBeDeleted != null}
            onClose={() => setUserToBeDeleted(null)}
            onSave={() => handleDeleteUser(userToBeDeleted)}
          />
          <div className="theme-blush component-wrapper">
            <div className="flex" style={{ alignItems: 'flex-start' }}>
              <div className="container-fluid" style={{ flex: '1 0 0' }}>
                <PageHeader
                  HeaderText="View Users"
                  Breadcrumb={[
                    { name: 'Users', navigate: '/view-users' },
                    { name: 'View Users', navigate: '/view-users' },
                  ]}
                />
              </div>
              <button
                onClick={() => navigate('/add-user')}
                className="btn btn-primary"
                style={{ marginTop: '20px', marginRight: '20px' }}
              >
                Add User
              </button>
            </div>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User ID</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.firstName}</TableCell>
                      <TableCell>{user.lastName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>+91{user.phoneNumber}</TableCell>
                      <TableCell style={{ padding: '5px' }}>
                        <Stack direction="row">
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/edit-user/${user.id}`);
                            }}
                          >
                            <i className="fa fa-pencil"></i>
                          </IconButton>
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              setUserToBeDeleted(user.id);
                            }}
                          >
                            <i className="fa fa-trash-o"></i>
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Pagination
              count={Math.ceil(totalUsers / pageSize)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'center',
              }}
            />
            <FormControl
              variant="outlined"
              style={{ minWidth: 120, marginBottom: '20px' }}
            >
              <InputLabel id="page-size-label">Page Size</InputLabel>
              <Select
                labelId="page-size-label"
                id="page-size-select"
                value={pageSize}
                onChange={handlePageSizeChange}
                label="Page Size"
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={20}>20</MenuItem>
              </Select>
            </FormControl>
          </div>
          </ProtectedComponent>
  );
}
